import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { TransitionTemplateSummaryListResponse } from '@/api/client';
import {
  deleteTransitionApiTransitionsTransitionIdDelete,
  getTransitionTemplatesApiTransitionTemplatesGet,
} from '@/api/client';

export const transitionTemplatesQueryKey = ['transition-templates'] as const;

export const getTransitionTemplates = () =>
  getTransitionTemplatesApiTransitionTemplatesGet({ limit: 500 });

/**
 * Transition templates for the property transition create form and the
 * templates management page.
 */
export const useTransitionTemplates = (
  options?: Omit<
    UseQueryOptions<TransitionTemplateSummaryListResponse>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery({
    queryKey: transitionTemplatesQueryKey,
    queryFn: getTransitionTemplates,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Deletes a transition template (or real transition) by id via
 * `DELETE /api/transitions/{transitionId}`. Invalidates the templates and
 * transitions-list queries so both views refresh.
 */
export const useDeleteTransition = (
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (transitionId: string) => {
      await deleteTransitionApiTransitionsTransitionIdDelete({ transitionId });
    },
    onSuccess: (data, transitionId, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: transitionTemplatesQueryKey,
      });
      void queryClient.invalidateQueries({
        queryKey: ['transitions', 'list'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['transition', transitionId],
      });
      options?.onSuccess?.(data, transitionId, onMutateResult, context);
    },
  });
};
